import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../../Common/DatasetSelector';
import DiscretizationLayout from '../../Common/DiscretizationLayout';

const MonotonicLightGBM = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [showTitleEditor, setShowTitleEditor] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const plotRef = useRef(null);
  
  const [plotTitle, setPlotTitle] = useState('');
  const [xAxisTitle, setXAxisTitle] = useState('');
  const [yAxisTitle, setYAxisTitle] = useState('Count');
  const [y2AxisTitle, setY2AxisTitle] = useState('Average Target');
  const [saveColName, setSaveColName] = useState('');
  
  const [formData, setFormData] = useState({
    Default_Date: null, Risk_driver: null, Target_variable: null,
    monotonicity: 'ascending', num_leaves: 4, max_depth: 3,
    min_data_in_leaf: 50, learning_rate: 0.1, num_boost_round: 1
  });

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  useEffect(() => {
    if (!window.Plotly) {
      const script = document.createElement('script');
      script.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js';
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (results?.bin_stats && formData.Risk_driver?.value && formData.Target_variable?.value) {
      setPlotTitle(`LightGBM: ${formData.Risk_driver.value} vs ${formData.Target_variable.value}`);
      setXAxisTitle(formData.Risk_driver.value);
      setSaveColName(`${formData.Risk_driver.value}_lgbm`);
    }
  }, [results, formData.Risk_driver, formData.Target_variable]);

  const renderPlot = (customTitles = null) => {
    if (!results?.bin_stats || !plotRef.current || !window.Plotly) return;
    const binStats = results.bin_stats;
    const categories = binStats.map(d => d.Bin);
    const counts = binStats.map(d => d.Count);
    const avgTargets = binStats.map(d => d.Avg_Target);
    const titles = customTitles || { plotTitle, xAxisTitle, yAxisTitle, y2AxisTitle };
    
    const trace1 = { x: categories, y: counts, type: 'bar', name: 'Count', marker: { color: '#e67e22', opacity: 0.7 }, yaxis: 'y' };
    const trace2 = { x: categories, y: avgTargets, type: 'scatter', mode: 'lines+markers', name: titles.y2AxisTitle, line: { color: '#e74c3c', width: 3 }, marker: { size: 8, color: '#e74c3c' }, yaxis: 'y2' };
    
    window.Plotly.newPlot(plotRef.current, [trace1, trace2], {
      title: { text: titles.plotTitle, font: { size: 16 } },
      xaxis: { title: titles.xAxisTitle, tickangle: -45 },
      yaxis: { title: titles.yAxisTitle, side: 'left' },
      yaxis2: { title: titles.y2AxisTitle, overlaying: 'y', side: 'right' },
      template: 'plotly_white', margin: { b: 100, r: 80 }, height: 500,
      legend: { orientation: 'h', y: 1.15 }
    }, { responsive: true });
  };

  useEffect(() => { 
    if (results?.bin_stats) { 
      const t = plotTitle || `${formData.Risk_driver?.value || ''} vs ${formData.Target_variable?.value || ''}`;
      setTimeout(() => renderPlot({ plotTitle: t, xAxisTitle: xAxisTitle || formData.Risk_driver?.value || '', yAxisTitle: yAxisTitle || 'Count', y2AxisTitle: y2AxisTitle || 'Average Target' }), 100); 
    } 
  }, [results]);

  const handleDatasetLoaded = (data) => {
    setColumns(data.columns || []); setDatasetInfo(data); setDatasetLoaded(true);
    toast.success(`Loaded ${data.rows?.toLocaleString()} rows`);
  };

  const handleSaveStep0 = async () => {
    if (!formData.Risk_driver || !formData.Target_variable) { toast.error('Required fields missing'); return; }
    setLoading(true);
    try {
      await axios.post('/api/monotonic-lightgbm/step0', {
        Risk_driver: formData.Risk_driver.value,
        Target_variable: formData.Target_variable.value,
        Default_Date: formData.Default_Date?.value || '',
        monotonicity: formData.monotonicity, num_leaves: formData.num_leaves,
        max_depth: formData.max_depth, min_data_in_leaf: formData.min_data_in_leaf,
        learning_rate: formData.learning_rate, num_boost_round: formData.num_boost_round
      });
      toast.success('Parameters saved'); setStep(1);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleRunAnalysis = async () => {
    setLoading(true);
    try {
      const res = await axios.post('/api/monotonic-lightgbm/step1', {
        Risk_driver: formData.Risk_driver.value,
        Target_variable: formData.Target_variable.value,
        Default_Date: formData.Default_Date?.value || '',
        monotonicity: formData.monotonicity, num_leaves: formData.num_leaves,
        max_depth: formData.max_depth, min_data_in_leaf: formData.min_data_in_leaf,
        learning_rate: formData.learning_rate, num_boost_round: formData.num_boost_round
      });
      setResults(res.data.results); toast.success(res.data.message); setStep(2);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleSaveColumn = async () => {
    if (!saveColName.trim()) { toast.error('Enter a column name'); return; }
    setLoading(true);
    try {
      await axios.post('/api/monotonic-lightgbm/step2', { new_column_name: saveColName.trim() });
      toast.success(`Saved as '${saveColName}'`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    try {
      const response = await axios.post('/api/monotonic-lightgbm/export-with-titles', { plotTitle, xAxisTitle, yAxisTitle, y2AxisTitle }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'lightgbm.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) { toast.error('Export failed'); }
  };

  const cs = { control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px', borderColor: '#e0e0e0' }), valueContainer: (b) => ({ ...b, padding: '0 8px' }), placeholder: (b) => ({ ...b, fontSize: '13px', color: '#95a5a6' }), menu: (b) => ({ ...b, zIndex: 9999, position: 'absolute' }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) };
  const gs = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' };
  const ghs = { background: '#f5f7fa', padding: '12px 16px', fontWeight: 600, fontSize: '14px', color: '#2c3e50' };
  const gcs = { padding: '16px', background: 'white' };
  const inputStyle = { width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px' };
  const btnStyle = (bg, disabled) => ({ padding: '10px 28px', background: disabled ? '#95a5a6' : bg, color: 'white', border: 'none', borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '13px' });

  return (
    <DiscretizationLayout title="Monotonic LightGBM" step={step} onStepClick={(t) => {
      if (t === 0) setStep(0); else if (t === 1 && datasetLoaded) setStep(1); else if (t === 2 && results) setStep(2);
    }} datasetLoaded={datasetLoaded} results={results}>
      
      {step === 0 && (
        <>
          <DatasetSelector apiPrefix="/api/monotonic-lightgbm" onDatasetLoaded={handleDatasetLoaded} />
          {columns.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <div style={gs}>
                <div style={ghs}>⚙️ Parameters</div>
                <div style={gcs}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Risk Driver *</label>
                      <Select options={columnOptions} value={formData.Risk_driver} onChange={v => setFormData({...formData, Risk_driver: v})} styles={cs} menuPortalTarget={document.body} placeholder="Select risk driver..." />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Target Variable *</label>
                      <Select options={columnOptions} value={formData.Target_variable} onChange={v => setFormData({...formData, Target_variable: v})} styles={cs} menuPortalTarget={document.body} placeholder="Select target variable..." />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Default Date (optional)</label>
                      <Select options={columnOptions} value={formData.Default_Date} onChange={v => setFormData({...formData, Default_Date: v})} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select date column..." />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button type="button" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                        style={{ width: '100%', padding: '10px 12px', fontSize: '13px', background: showAdvancedOptions ? '#e67e22' : '#f8f9fa', color: showAdvancedOptions ? 'white' : '#7f8c8d', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}>
                        {showAdvancedOptions ? '✓ Advanced Options' : '⚙️ Advanced Options'}
                      </button>
                    </div>
                  </div>

                  {showAdvancedOptions && (
                    <div style={{ padding: '16px', background: '#f9f9f9', borderRadius: '6px', marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div><label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Monotonicity</label><select value={formData.monotonicity} onChange={(e) => setFormData({...formData, monotonicity: e.target.value})} style={inputStyle}><option value="ascending">Ascending</option><option value="descending">Descending</option></select></div>
                      <div><label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>num_leaves</label><input type="number" value={formData.num_leaves} onChange={(e) => setFormData({...formData, num_leaves: parseInt(e.target.value)})} min="2" max="256" style={inputStyle} /></div>
                      <div><label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>max_depth</label><input type="number" value={formData.max_depth} onChange={(e) => setFormData({...formData, max_depth: parseInt(e.target.value)})} min="-1" max="20" style={inputStyle} /><small style={{ color: '#7f8c8d', fontSize: '10px' }}>-1 = no limit</small></div>
                      <div><label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>min_data_in_leaf</label><input type="number" value={formData.min_data_in_leaf} onChange={(e) => setFormData({...formData, min_data_in_leaf: parseInt(e.target.value)})} min="1" style={inputStyle} /></div>
                      <div><label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>learning_rate</label><input type="number" step="0.01" value={formData.learning_rate} onChange={(e) => setFormData({...formData, learning_rate: parseFloat(e.target.value)})} min="0.001" max="1" style={inputStyle} /></div>
                      <div><label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>num_boost_round</label><input type="number" value={formData.num_boost_round} onChange={(e) => setFormData({...formData, num_boost_round: parseInt(e.target.value)})} min="1" max="1000" style={inputStyle} /></div>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={handleSaveStep0} disabled={loading} style={btnStyle('#e67e22', loading)}>
                      {loading ? 'Saving...' : 'Save & Continue →'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {step === 1 && (
        <div>
          <div style={gs}><div style={ghs}>📋 Parameters Summary</div><div style={gcs}><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}><p><strong>Dataset:</strong> {datasetInfo?.rows?.toLocaleString()} rows</p><p><strong>Risk Driver:</strong> {formData.Risk_driver?.label}</p><p><strong>Target:</strong> {formData.Target_variable?.label}</p><p><strong>Monotonicity:</strong> {formData.monotonicity}</p><p><strong>num_leaves:</strong> {formData.num_leaves}</p></div></div></div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={() => setStep(0)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={handleRunAnalysis} disabled={loading} style={btnStyle('#3498db', loading)}>{loading ? 'Running...' : '🚀 Run LightGBM'}</button>
          </div>
        </div>
      )}

      {step === 2 && results && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <div style={{ textAlign: 'center', padding: '16px 32px', background: 'white', borderRadius: '8px', border: '2px solid #e67e22', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#e67e22' }}>{results.gauc_value}</div>
              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>gAUC (Somers' D)</div>
            </div>
          </div>

          <div style={gs}>
            <div style={{ ...ghs, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📈 Chart</span>
              <button onClick={() => setShowTitleEditor(!showTitleEditor)} style={{ padding: '2px 8px', fontSize: '11px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>{showTitleEditor ? '✕ Close' : '✏️ Edit Titles'}</button>
            </div>
            <div style={gcs}>
              {showTitleEditor && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', padding: '12px', background: '#f9f9f9', borderRadius: '6px', marginBottom: '12px' }}>
                  <div><label style={{ fontSize: '11px', fontWeight: 500 }}>Chart Title</label><input type="text" value={plotTitle} onChange={e => setPlotTitle(e.target.value)} style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>
                  <div><label style={{ fontSize: '11px', fontWeight: 500 }}>X Axis</label><input type="text" value={xAxisTitle} onChange={e => setXAxisTitle(e.target.value)} style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>
                  <div><label style={{ fontSize: '11px', fontWeight: 500 }}>Y Axis (Count)</label><input type="text" value={yAxisTitle} onChange={e => setYAxisTitle(e.target.value)} style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>
                  <div><label style={{ fontSize: '11px', fontWeight: 500 }}>Y Axis (Target)</label><input type="text" value={y2AxisTitle} onChange={e => setY2AxisTitle(e.target.value)} style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px' }}>
                    <button onClick={() => { renderPlot({ plotTitle, xAxisTitle, yAxisTitle, y2AxisTitle }); setShowTitleEditor(false); }}
                      style={{ padding: '6px 16px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Apply</button>
                    <button onClick={() => { setPlotTitle(`LightGBM: ${formData.Risk_driver?.value} vs ${formData.Target_variable?.value}`); setXAxisTitle(formData.Risk_driver?.value || ''); setYAxisTitle('Count'); setY2AxisTitle('Average Target'); }}
                      style={{ padding: '6px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Reset</button>
                  </div>
                </div>
              )}
              <div ref={plotRef} style={{ width: '100%', minHeight: '500px' }}></div>
            </div>
          </div>

          <div style={gs}>
            <div style={ghs}>📊 Bin Statistics</div>
            <div style={gcs}>
              <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#2c3e50', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={{ color: 'white', fontWeight: 600, padding: '8px 12px', textAlign: 'left', fontSize: '11px' }}>Bin</th>
                      <th style={{ color: 'white', fontWeight: 600, padding: '8px 12px', textAlign: 'left', fontSize: '11px' }}>Count</th>
                      <th style={{ color: 'white', fontWeight: 600, padding: '8px 12px', textAlign: 'left', fontSize: '11px' }}>Avg Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.bin_stats?.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{row.Bin}</td>
                        <td style={{ padding: '6px 12px' }}>{row.Count?.toLocaleString()}</td>
                        <td style={{ padding: '6px 12px' }}>{row.Avg_Target?.toFixed(6)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div><label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 4 }}>Column Name</label><input type="text" value={saveColName} onChange={e => setSaveColName(e.target.value)} style={{ padding: '8px 12px', fontSize: 12, border: '1px solid #ddd', borderRadius: 6, width: 180 }} /></div>
              <button onClick={handleSaveColumn} disabled={loading} style={{ padding: '8px 20px', background: loading ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', borderRadius: 6, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>{loading ? 'Saving...' : '💾 Save'}</button>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStep(1)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>← Back</button>
              <button onClick={handleExport} style={{ padding: '8px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>📥 Export</button>
            </div>
          </div>
        </div>
      )}
    </DiscretizationLayout>
  );
};

export default MonotonicLightGBM;